import React, { useState } from 'react';
import { Show } from '../../types';
import ShowForm from './ShowForm';
import { api } from '../../services/api';
import { formatShowDate, sortShowsByDate } from '../../utils/dateUtils';

interface ShowManagerProps {
  shows: Show[];
  onUpdate: () => void;
}

const ShowManager: React.FC<ShowManagerProps> = ({ shows, onUpdate }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingShow, setEditingShow] = useState<Show | null>(null);

  const handleCreateShow = async (showData: Omit<Show, 'id'>) => {
    try {
      await api.createShow(showData);
      setIsFormOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to create show:', error);
    }
  };

  const handleUpdateShow = async (id: string, showData: Partial<Show>) => {
    try {
      await api.updateShow(id, showData);
      setEditingShow(null);
      setIsFormOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update show:', error);
    }
  };

  const handleDeleteShow = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this show?')) {
      try {
        await api.deleteShow(id);
        onUpdate();
      } catch (error) {
        console.error('Failed to delete show:', error);
      }
    }
  };

  const handleEdit = (show: Show) => {
    setEditingShow(show);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingShow(null);
  };

  return (
    <div>
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">Shows</h2>
            <button
              onClick={() => setIsFormOpen(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Add Show
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {shows.length === 0 ? (
            <p className="px-6 py-4 text-gray-500">No shows scheduled</p>
          ) : (
            sortShowsByDate(shows)
              .map(show => (
                  <div key={show.id} className="px-6 py-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {show.venue.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {formatShowDate(show.date)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {show.startTime} {show.duration && `(${show.duration})`} • {
                        show.bandConfig === 'duet' ? 'Duet' :
                        show.bandConfig === 'trio' ? 'Trio' :
                        'Full Band'
                      }
                    </p>
                    {(show.eventType === 'private' || show.isPrivate) && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                        Private
                      </span>
                    )}
                    {show.eventType === 'internet' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                        Internet
                      </span>
                    )}
                    {(show.eventType === 'ticketed' || show.isTicketed) && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                        Ticketed
                      </span>
                    )}
                    {show.isCancelled && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1 ml-1">
                        Cancelled
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(show)}
                      className="text-blue-600 hover:text-blue-900 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteShow(show.id)}
                      className="text-red-600 hover:text-red-900 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              ))
          )}
        </div>
      </div>

      {/* Show Form Modal */}
      {isFormOpen && (
        <ShowForm
          show={editingShow}
          onSubmit={editingShow 
            ? (data) => handleUpdateShow(editingShow.id, data)
            : handleCreateShow
          }
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default ShowManager;